import React, { useState } from "react"

import { CheckCircle2, ChevronDown, ChevronRight } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import type { ApplicationDetails } from "@/hooks/useApplicationDetails"

interface TrialBlocksListProps {
  trialBlocks: ApplicationDetails["trial_blocks"]
}

const TrialBlocksList = ({ trialBlocks }: TrialBlocksListProps) => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(true)

  return (
    <Card className="shadow-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg font-semibold">Trial Blocks</CardTitle>
              </div>
              {isOpen ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {trialBlocks.length > 0 ? (
              <div className="space-y-4">
                {trialBlocks.map((block) => (
                  <div key={block.id} className="border rounded-md p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium">
                          Trial on{" "}
                          {new Date(block.date).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </p>
                        <p className="text-sm text-palette-jungleGreen500">{block.location}</p>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          block.result === "Passed"
                            ? "bg-green-100 text-green-800 border-green-200"
                            : block.result === "Failed"
                              ? "bg-red-100 text-red-800 border-red-200"
                              : "bg-yellow-100 text-yellow-800 border-yellow-200"
                        }
                      >
                        {block.result ? block.result : "Pending"}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <p className="text-xs text-palette-jungleGreen500">Vehicle</p>
                        <p className="text-sm font-medium">{block.vehicle_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-palette-jungleGreen500">Route Code</p>
                        <p className="text-sm font-medium">{block.route_code}</p>
                      </div>
                    </div>
                    {block.comments && (
                      <div className="mt-2">
                        <p className="text-xs text-palette-jungleGreen500">Notes</p>
                        <p className="text-sm bg-palette-jungleGreen000 p-2 rounded mt-1">
                          {block.comments}
                        </p>
                      </div>
                    )}
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/trial-blocks?id=${block.id}`)}
                        disabled
                      >
                        <CheckCircle2 className="mr-1 h-4 w-4" />
                        View Trial Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-palette-jungleGreen500">
                No trial blocks found for this applicant.
              </p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

export default TrialBlocksList
